import React, { useState, useEffect, useRef } from 'react';
import { containsBadWord } from '../utils/badWordFilter';
import { shareRoot } from '../utils/shareRoot';

import { LOADING_EMOJIS, scoreToDotColor, scoreToCellBackground } from '../utils/scoreColors';

function EmojiLoader() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((i) => (i + 1) % LOADING_EMOJIS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return <span className="text-2xl">{LOADING_EMOJIS[index]}</span>;
}

function SlowWarning({ state }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    setShow(false);
    const timer = setTimeout(() => setShow(true), 4000);
    return () => clearTimeout(timer);
  }, [state]);

  if (!show) return null;
  return <p className="text-[9px] sm:text-[10px] text-ink-faint mt-1 font-body italic">taking longer than usual...</p>;
}

export default function GridCell({ cell, rowAdj, colAdj, onSubmit, onRetry, canRetry, CELL_STATES, isFocusTarget, onFocus, grid }) {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isFocusTarget && inputRef.current && (cell.state === CELL_STATES.EMPTY || cell.state === CELL_STATES.RETRYING)) {
      inputRef.current.focus();
    }
  }, [isFocusTarget, cell.state, CELL_STATES]);

  const handleSubmit = () => {
    const word = inputValue.trim().toLowerCase();
    if (!word) return;
    if (!/^[a-z]+$/.test(word)) {
      setError('Letters only');
      return;
    }
    if (word.length < 4) {
      setError('At least 4 letters');
      return;
    }
    if (containsBadWord(word)) {
      setError('Please use appropriate language');
      return;
    }
    // Check against row/col adjective roots
    if (shareRoot(word, rowAdj) || shareRoot(word, colAdj)) {
      setError('Too similar to a grid label');
      return;
    }
    // Check for duplicate / shared-root words already in the grid
    if (grid) {
      const duplicate = grid.flat().some(
        (c) => c !== cell && c.finalWord && c.finalWord !== '\u2014' && shareRoot(word, c.finalWord)
      );
      if (duplicate) {
        setError('You already submitted a similar word');
        return;
      }
    }
    setError('');
    onSubmit(word, cell.state);
    setInputValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  // EMPTY or RETRYING: show input
  if (cell.state === CELL_STATES.EMPTY || cell.state === CELL_STATES.RETRYING) {
    return (
      <div className={`flex flex-col items-center justify-center h-full p-1 sm:p-2${isFocused && cell.state === CELL_STATES.EMPTY ? ' bg-gray-100' : ''}`}>
        <input
          ref={inputRef}
          type="text"
          className="w-full text-xs sm:text-base font-body text-center px-0 sm:px-1 border-0 border-b border-ink bg-transparent outline-none"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value.replace(/[^a-z]/g, ''));
            setError('');
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => { setIsFocused(true); onFocus?.(); }}
          onBlur={() => setIsFocused(false)}
          placeholder={cell.state === CELL_STATES.RETRYING ? 'Retry' : ''}
          aria-label={`Enter word for ${rowAdj} and ${colAdj}`}
          autoComplete="off"
          maxLength={30}
        />
        {error && <p className="text-red-600 text-[10px] sm:text-xs mt-1 font-body">{error}</p>}
        {inputValue.trim() && (
          <button
            onClick={handleSubmit}
            className="mt-1 sm:mt-2 px-2 sm:px-3 py-1 text-xs sm:text-sm font-body font-semibold bg-ink text-white hover:bg-ink-light transition-colors min-h-0"
          >
            Submit
          </button>
        )}
        {cell.state === CELL_STATES.RETRYING && cell.attempt1 && (
          <p className="text-[10px] sm:text-xs text-ink-faint mt-1 font-body">
            Prev: {cell.attempt1.word}
          </p>
        )}
      </div>
    );
  }

  // SUBMITTING or RESUBMITTING: cycling emoji loader with slow warning
  if (cell.state === CELL_STATES.SUBMITTING || cell.state === CELL_STATES.RESUBMITTING) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <EmojiLoader />
        <SlowWarning state={cell.state} />
      </div>
    );
  }

  // FINALIZED: locked in with discrete color background, no score numbers
  if (cell.state === CELL_STATES.FINALIZED) {
    return (
      <div
        className="flex flex-col items-center justify-center h-full p-1 sm:p-2"
        style={{ backgroundColor: scoreToCellBackground(cell.finalScore) }}
      >
        <p className="font-serif font-bold text-xs sm:text-lg text-ink break-all text-center leading-tight">
          {cell.finalWord}
        </p>
        {cell.attempt1 && (
          <div className="flex items-center gap-1.5 sm:gap-2 mt-1.5">
            <span
              className="inline-block w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full"
              style={{ backgroundColor: scoreToDotColor(cell.attempt1.adj1Score) }}
            />
            <span
              className="inline-block w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full"
              style={{ backgroundColor: scoreToDotColor(cell.attempt1.adj2Score) }}
            />
          </div>
        )}
        {cell.finalScore != null && (
          <p
            className="text-[10px] sm:text-xs font-body font-semibold text-white inline-block px-1.5 py-0.5 rounded-full mt-1"
            style={{ backgroundColor: scoreToDotColor(cell.finalScore) }}
          >
            {cell.finalScore.toFixed(1)}
          </p>
        )}
        {canRetry && !cell.attempt2 && (
          <button
            onClick={onRetry}
            className="mt-1 px-2 py-0.5 text-[10px] sm:text-xs font-body font-semibold bg-white text-ink border border-ink hover:bg-gray-100 transition-colors"
          >
            <span className="sm:hidden">{'\u21BB'}</span>
            <span className="hidden sm:inline">Retry</span>
          </button>
        )}
        {cell.losingWord && (
          <p className="text-xs text-ink-faint mt-1 font-body line-through">{cell.losingWord}</p>
        )}
      </div>
    );
  }

  return null;
}

